// src/context/walletNetworkHelpers.jsx

import { getWalletProvider, getChainInfo, getOkxSolanaProvider } from '../utils/walletUtils';

/**
 * Handle network switching specifically for OKX wallet
 * @param {string} chainId - The chain ID to switch to
 * @param {Object} wallet - The current wallet state
 * @param {Function} showNotification - Function to show notifications
 * @param {Function} disconnectWallet - Function to disconnect wallet
 * @param {Function} connectWallet - Function to connect wallet
 * @param {Function} setWallet - Function to update wallet state
 * @returns {Promise<boolean>} Success status
 */
export async function handleOkxNetworkSwitch(
  chainId, 
  wallet, 
  showNotification, 
  disconnectWallet, 
  connectWallet, 
  setWallet
) {
  // If switching between EVM and Solana in OKX, we need to disconnect and reconnect
  if ((wallet.network === 'solana' && (chainId === 'ethereum' || chainId === 'bsc')) ||
      ((wallet.network === 'ethereum' || wallet.network === 'bsc') && chainId === 'solana')) {
    
    console.log('Switching between EVM and Solana requires reconnection');
    
    // Disconnect current wallet
    disconnectWallet();
    
    // Wait briefly for disconnection to complete
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Reconnect with the desired chain preference
    if (chainId === 'solana') {
      await connectWallet('okx', 'solana');
    } else {
      await connectWallet('okx', 'ethereum');
    }
    
    return true;
  }
  
  const provider = getWalletProvider(wallet.type);
  
  if (!provider) {
    throw new Error('OKX wallet provider not available');
  }
  
  // For OKX staying in EVM chains, we'll try multiple methods
  
  // First: Try OKX-specific methods
  if (provider.switchNetwork) {
    // Some OKX versions provide this method
    try {
      console.log('Trying OKX switchNetwork method');
      await provider.switchNetwork(
        chainId === 'bsc' ? '0x38' : '0x1'
      );
    } catch (err) {
      console.warn('OKX switchNetwork failed:', err);
      // Continue to other methods
    }
  }
  
  // Second: Try wallet_switchEthereumChain (standard method)
  try {
    console.log('Trying wallet_switchEthereumChain method');
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: chainId === 'bsc' ? '0x38' : '0x1' }],
    });
  } catch (err) {
    console.warn('wallet_switchEthereumChain failed:', err);
    
    // If chain not added, try to add it
    if (err.code === 4902) {
      const networks = {
        ethereum: {
          chainId: '0x1',
          chainName: 'Ethereum Mainnet',
          nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
          rpcUrls: ['https://mainnet.infura.io/v3/'],
          blockExplorerUrls: ['https://etherscan.io/']
        },
        bsc: {
          chainId: '0x38',
          chainName: 'Binance Smart Chain',
          nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
          rpcUrls: ['https://bsc-dataseed.binance.org/'],
          blockExplorerUrls: ['https://bscscan.com/']
        }
      };
      
      try {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [networks[chainId]],
        });
      } catch (addErr) {
        console.error('Failed to add network to OKX:', addErr);
        // Continue to other methods
      }
    }
  }
  
  // Third: Try other OKX-specific methods
  try {
    // Some versions might use eth_switchNetwork
    if (provider.request && typeof provider.request === 'function') {
      console.log('Trying eth_switchNetwork method');
      await provider.request({
        method: 'eth_switchNetwork',
        params: [chainId === 'bsc' ? '0x38' : '0x1'],
      });
    }
  } catch (err) {
    console.warn('eth_switchNetwork failed:', err);
    // Continue to final attempt
  }
  
  // Wait briefly for the network change to propagate
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Check if chain actually changed
  const chainInfo = await getChainInfo(provider, 'okx');
  
  // If chain didn't change to desired network, throw error
  if ((chainId === 'bsc' && chainInfo.network !== 'bsc') ||
      (chainId === 'ethereum' && chainInfo.network !== 'ethereum')) {
    throw new Error('Failed to switch network in OKX wallet. Please try switching networks manually in the OKX wallet interface.');
  }
  
  // Update wallet state
  const updatedWallet = {
    ...wallet,
    chainId: chainInfo.chainId,
    chainName: chainInfo.name,
    network: chainInfo.network,
    balance: chainInfo.balance,
    usdtBalance: chainInfo.usdtBalance || '0.00',
  };
  
  setWallet(updatedWallet);
  
  // Update localStorage
  localStorage.setItem('aerofi_wallet', JSON.stringify(updatedWallet));
  
  // Show success notification
  showNotification(`Switched to ${chainInfo.name}`);
    
  return true;
}

/**
 * Handles wallet event setup for different wallet types
 * @param {Object} provider - The wallet provider
 * @param {Object} wallet - Current wallet state
 * @param {Function} disconnectWallet - Function to disconnect wallet
 * @param {Function} updateWalletData - Function to update wallet data
 * @returns {Function} Cleanup function
 */
export function setupWalletEvents(provider, wallet, disconnectWallet, updateWalletData) {
  const isSolana = wallet.network === 'solana';
  
  // Handle OKX Solana
  if (wallet.type.toLowerCase() === 'okx' && isSolana) {
    console.log('Setting up OKX Solana event listeners');
    
    // Setup event listeners for OKX Solana
    if (provider.on && typeof provider.on === 'function') {
      // Standard events
      provider.on('connect', () => console.log('OKX Solana connected'));
      provider.on('disconnect', disconnectWallet);
      
      // Account change events - different wallet versions use different event names
      const handleAccountChange = (publicKey) => {
        if (publicKey) {
          updateWalletData(publicKey);
        } else {
          disconnectWallet();
        }
      };
      
      provider.on('accountChanged', handleAccountChange);
      provider.on('accountChange', handleAccountChange);
      
      return () => {
        // Clean up all listeners
        if (typeof provider.removeListener === 'function') {
          provider.removeListener('connect', () => console.log('OKX Solana connected'));
          provider.removeListener('disconnect', disconnectWallet);
          provider.removeListener('accountChanged', handleAccountChange);
          provider.removeListener('accountChange', handleAccountChange);
        }
      };
    }
  } 
  // Handle other Solana wallets
  else if (isSolana) {
    console.log('Setting up Solana wallet event listeners');
    
    if (provider.on && typeof provider.on === 'function') {
      provider.on('disconnect', disconnectWallet);
      
      const handleAccountChange = (publicKey) => {
        if (publicKey) {
          updateWalletData(publicKey);
        } else {
          disconnectWallet();
        }
      };
      
      provider.on('accountChange', handleAccountChange);
      
      // Fallback for wallets that don't properly implement events
      window.addEventListener('popstate', disconnectWallet);
      
      return () => {
        // Clean up
        if (provider.removeListener && typeof provider.removeListener === 'function') {
          provider.removeListener('disconnect', disconnectWallet);
          provider.removeListener('accountChange', handleAccountChange);
        }
        window.removeEventListener('popstate', disconnectWallet);
      };
    }
  } 
  // Handle EVM wallets
  else if (provider.on && typeof provider.on === 'function') {
    console.log('Setting up EVM wallet event listeners');
    
    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        // User disconnected their wallet
        disconnectWallet();
      } else if (accounts[0] !== wallet.address) {
        // Account changed, update wallet state
        updateWalletData(accounts[0]);
      }
    };
    
    const handleChainChanged = async () => {
      try {
        // Refetch chain info after a short delay to ensure it's updated
        setTimeout(async () => {
          const chainInfo = await getChainInfo(provider, wallet.type);
          updateWalletData(wallet.address, chainInfo);
        }, 500);
      } catch (error) {
        console.error('Error handling chain change:', error);
      }
    };
    
    provider.on('accountsChanged', handleAccountsChanged);
    provider.on('chainChanged', handleChainChanged);
    provider.on('disconnect', disconnectWallet);
    
    return () => {
      // Clean up
      if (typeof provider.removeListener === 'function') {
        provider.removeListener('accountsChanged', handleAccountsChanged);
        provider.removeListener('chainChanged', handleChainChanged);
        provider.removeListener('disconnect', disconnectWallet);
      }
    };
  }
  
  // Return empty cleanup function if no event listeners were added
  return () => {};
}

/**
 * Get network configuration for wallet_addEthereumChain method
 * @param {string} chainId - The chain ID
 * @returns {Object} Network configuration
 */
export function getNetworkConfig(chainId) {
  const networks = {
    ethereum: {
      chainId: '0x1', // 1
      chainName: 'Ethereum Mainnet',
      nativeCurrency: {
        name: 'Ether',
        symbol: 'ETH',
        decimals: 18
      },
      rpcUrls: ['https://mainnet.infura.io/v3/'],
      blockExplorerUrls: ['https://etherscan.io/']
    },
    bsc: {
      chainId: '0x38', // 56
      chainName: 'Binance Smart Chain',
      nativeCurrency: {
        name: 'BNB',
        symbol: 'BNB',
        decimals: 18
      },
      rpcUrls: ['https://bsc-dataseed.binance.org/'],
      blockExplorerUrls: ['https://bscscan.com/']
    },
    polygon: {
      chainId: '0x89', // 137
      chainName: 'Polygon Mainnet',
      nativeCurrency: {
        name: 'MATIC',
        symbol: 'MATIC',
        decimals: 18
      },
      rpcUrls: ['https://polygon-rpc.com/'],
      blockExplorerUrls: ['https://polygonscan.com/']
    },
    arbitrum: {
      chainId: '0xa4b1', // 42161
      chainName: 'Arbitrum One',
      nativeCurrency: {
        name: 'Ethereum',
        symbol: 'ETH',
        decimals: 18
      },
      rpcUrls: ['https://arb1.arbitrum.io/rpc'],
      blockExplorerUrls: ['https://arbiscan.io/']
    },
    optimism: {
      chainId: '0xa', // 10
      chainName: 'Optimism',
      nativeCurrency: {
        name: 'Ethereum',
        symbol: 'ETH',
        decimals: 18
      },
      rpcUrls: ['https://mainnet.optimism.io'],
      blockExplorerUrls: ['https://optimistic.etherscan.io/']
    },
    avalanche: {
      chainId: '0xa86a', // 43114
      chainName: 'Avalanche C-Chain',
      nativeCurrency: {
        name: 'Avalanche',
        symbol: 'AVAX',
        decimals: 18
      },
      rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'],
      blockExplorerUrls: ['https://snowtrace.io/']
    }
  };

  return networks[chainId] || null;
}
